import { useCallback, useState } from 'react';
import { Badge, ButtonColor, ButtonVariant, Icon, IconButton, Icons, TextButton, TextInput } from '../../components';
import { useHandleApiResponse, useToast } from '../../hooks';
import { useUpdateOrganizationSettingsMutation } from '../../services';
import { ComponentSize } from '../../types';
import OrgTabSectionTitle from './OrgTabSectionTitle';

const UPDATE_VOCABULARY_ERROR_MSG = 'Failed to update vocabulary. Please try again later.';

enum VocabularyAction {
  ADD = 'add',
  REMOVE = 'remove',
}

interface CustomVocabularyProps {
  vocabulary: string[];
}

const CustomVocabulary = ({ vocabulary }: CustomVocabularyProps) => {
  const [newVocab, setNewVocab] = useState('');
  // We are using this state to show a loading state on the Add button when adding a new word
  // and to disable the Add button when removing a word.
  const [currAction, setCurrAction] = useState<VocabularyAction | null>(null);
  const [error, setError] = useState('');

  // API Mutation
  const [updateOrganizationSettings] = useUpdateOrganizationSettingsMutation();

  // Custom Hooks
  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Handle updating vocabulary
  const handleUpdateCustomVocabulary = useCallback(
    async (word: string, action: VocabularyAction) => {
      setCurrAction(action);
      const newVocabulary =
        action === VocabularyAction.ADD ? [...vocabulary, word] : vocabulary.filter((w) => w !== word);
      try {
        const response = await updateOrganizationSettings({
          vocabulary: newVocabulary,
        });
        handleApiResponse({
          response,
          errorMsg: UPDATE_VOCABULARY_ERROR_MSG,
          onError: (error) => setError(error.message),
          onSuccess: action === VocabularyAction.ADD ? () => setNewVocab('') : undefined,
        });
      } catch (error) {
        console.error(error);
        setError(UPDATE_VOCABULARY_ERROR_MSG);
      } finally {
        setCurrAction(null);
      }
    },
    [vocabulary, updateOrganizationSettings, handleApiResponse, showToast, setCurrAction, setNewVocab]
  );

  // Handle adding new vocabulary
  const handleAddVocab = useCallback(async () => {
    // Check if word is empty.
    // Needed to stop Enter from adding a word.
    if (!newVocab.trim()) return;
    // Check if word already exists (case-insensitive)
    const isWordAlreadyExists = vocabulary.some((word) => word.toLowerCase() === newVocab.toLowerCase());
    if (isWordAlreadyExists) {
      setError('This word already exists');
    } else {
      handleUpdateCustomVocabulary(newVocab, VocabularyAction.ADD);
    }
  }, [newVocab, vocabulary, handleUpdateCustomVocabulary]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setNewVocab(e.target.value);
  }, []);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleAddVocab();
      }
    },
    [handleAddVocab]
  );

  return (
    <div className="flex flex-col gap-4">
      <OrgTabSectionTitle
        title="Custom vocabulary"
        icon={
          <Icons
            icon={Icon.INFO}
            tooltip="Improve transcription by adding specialized terminology or uncommon proper nouns."
          />
        }
      />
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-4">
          <TextInput
            placeholder="e.g. Your organization name"
            value={newVocab}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            error={error}
          />
          <TextButton
            text="Add"
            onClick={handleAddVocab}
            disabled={!newVocab.trim() || currAction === VocabularyAction.REMOVE}
            loading={currAction === VocabularyAction.ADD}
          />
        </div>
        {!!vocabulary?.length && (
          <div className="flex flex-wrap gap-2">
            {vocabulary.map((word) => (
              <Badge
                size={ComponentSize.MEDIUM}
                key={word}
                label={word}
                endElement={
                  <IconButton
                    icon={Icon.CLOSE}
                    size={ComponentSize.X_SMALL}
                    color={ButtonColor.NEUTRAL}
                    variant={ButtonVariant.GHOST}
                    onClick={() => handleUpdateCustomVocabulary(word, VocabularyAction.REMOVE)}
                  />
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomVocabulary;
